import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  FiscalRepDocumentFragment,
  FiscalRepDocumentType,
  NifOrderDocumentFragment,
} from '@src/generated/graphql';
import { notEmpty } from '@src/utils/array';
import { DocumentEntry } from '../components/DocumentsTable/DocumentsTable';

const FISCAL_DOC_TYPE_TO_NAME: Record<FiscalRepDocumentType, string> = {
  // t('documents.fiscal_rep_document_type.imposto_municipal_sobre_imoveis', 'Imposto Municipal sobre Imóveis')
  IMPOSTO_MUNICIPAL_SOBRE_IMOVEIS:
    'documents.fiscal_rep_document_type.imposto_municipal_sobre_imoveis',
  // t('documents.fiscal_rep_document_type.imposto_unico_circulacao', 'Imposto Único de Circulação')
  IMPOSTO_UNICO_CIRCULACAO:
    'documents.fiscal_rep_document_type.imposto_unico_circulacao',
  // t('documents.fiscal_rep_document_type.password', 'Portal das Finanças Password')
  PASSWORD: 'documents.fiscal_rep_document_type.password',
  // t('documents.fiscal_rep_document_type.other', 'Other')
  OTHER: 'other',
};

const getRecipientName = (
  doc: FiscalRepDocumentFragment['subscription'] | NifOrderDocumentFragment,
): string => `${doc.givenNames} ${doc.surname}`;

function prepareNifDocumentEntry(
  doc: NifOrderDocumentFragment,
  t: TFunction,
): DocumentEntry | undefined {
  if (!doc.completedAt || !doc.nifDocumentFileId) {
    return undefined;
  }

  const additionalInfo = (() => {
    if (doc.nifNumber) {
      return t(
        'documents.additional_info.nif_document',
        'NIF Number: {{nifNumber}}',
        {
          nifNumber: doc.nifNumber,
        },
      );
    }
  })();

  return {
    id: doc.id,
    fileId: doc.nifDocumentFileId,
    recipient: getRecipientName(doc),
    documentType: 'NIF',
    additionalInfo,
    createdAt: doc.completedAt,
  };
}

function prepareFiscalRepDocumentEntry(
  doc: FiscalRepDocumentFragment,
  t: TFunction,
): DocumentEntry {
  const additionalInfo = (() => {
    if (doc.portalPassword) {
      return t('documents.additional_info.password', 'Password: {{password}}', {
        password: doc.portalPassword,
      });
    }
  })();

  const additionalInfoTooltip = (() => {
    if (doc.portalPassword) {
      return t(
        'documents.additional_info.password_tooltip',
        'Your password was automatically extracted and may contain errors. If you encounter login issues on Portal das Finanças, download your password document and see the "Senha de Acesso Internet" section for your password.',
      );
    }
  })();

  const manualT = t;

  return {
    id: doc.id,
    createdAt: doc.createdAt,
    recipient: getRecipientName(doc.subscription),
    documentType: manualT(FISCAL_DOC_TYPE_TO_NAME[doc.type]),
    additionalInfo,
    additionalInfoTooltip,
    fileId: doc.fileId,
  };
}

function prepareDocumentEntry(
  doc: FiscalRepDocumentFragment | NifOrderDocumentFragment,
  t: TFunction,
): DocumentEntry | undefined {
  switch (doc.__typename) {
    case 'FiscalRepDocument':
      return prepareFiscalRepDocumentEntry(doc, t);
    case 'NifOrder':
      return prepareNifDocumentEntry(doc, t);
    default:
      throw new Error(`Unhandled document type: ${doc.__typename}`);
  }
}

export function usePrepareDocumentEntries(): (
  fiscalRepDocuments: FiscalRepDocumentFragment[],
  nifDocuments: NifOrderDocumentFragment[],
) => DocumentEntry[] {
  const { t } = useTranslation();

  return (fiscalRepDocuments, nifDocuments) =>
    [...fiscalRepDocuments, ...nifDocuments]
      .map((doc) => prepareDocumentEntry(doc, t))
      .filter(notEmpty);
}
